import React, { useState, useRef, useEffect } from "react"
import ReactHtmlParser from "react-html-parser"
import Svg from "../_svgs/svg"
import HotspotSvg from "./hotspot-svg-component"
import * as SelectionFactoryHelper from "../helpers/SelectionFactoryHelper"
import useWindowWidth from "../../hooks/useWindowWidth"

export default function HotspotImageBlockComponent({ title, infographic, focalPoints, bottomPadding, topPadding, ariaLabelOpen, ariaLabelClose }) {
  const [selectedFocal, setSelectedFocal] = useState(null)
  const [activePoint, setActivePoint] = useState(null)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [translateX, setTranslateX] = useState(0)
  const [translateY, setTranslateY] = useState(0)

  const windowWidth = useWindowWidth()
  const tp = SelectionFactoryHelper.getFromSelectionFactory(topPadding)
  const bp = SelectionFactoryHelper.getFromSelectionFactory(bottomPadding)

  const buttonRefs = useRef([])
  const closeButtonRef = useRef(null)
  const previousSelectedRef = useRef(null)

  const handleClose = () => {
    if (selectedFocal !== null) {
      previousSelectedRef.current = selectedFocal
    }
    setSelectedFocal(null)
    setActivePoint(null)
    setZoomLevel(1)
    setTranslateX(0)
    setTranslateY(0)
  }

  useEffect(() => {
    if (selectedFocal !== null) {
      closeButtonRef.current?.focus()
    } else {
      const prev = previousSelectedRef.current
      if (prev !== null && buttonRefs.current[prev]) {
        buttonRefs.current[prev].focus()
      }
      previousSelectedRef.current = null
    }
  }, [selectedFocal])

  const imageAndPointsStyle = {
    transform: `scale(${zoomLevel}) translate(${translateX}%, ${translateY}%)`,
  }

  return (
    <div className={`hotspot-image-block-container ${tp || ""} ${bp || ""}`}>
      <div className="w--wider">
        <div data-epi-property="Title" className="main-text">
          {ReactHtmlParser(title)}
        </div>
        <div className="hotspot-image-border">
          <div className="hotspot-image-container">
            <div style={imageAndPointsStyle}>
              <div className="hotspot-image">{infographic ? <img src={infographic} alt="" className="infographic" /> : <HotspotSvg />}</div>
              <div className="icons">
                {focalPoints.map((point, index) => (
                  <FocalPoint
                    key={index}
                    point={point}
                    active={index === selectedFocal}
                    index={index}
                    selectedFocal={selectedFocal}
                    setSelectedFocal={setSelectedFocal}
                    setActivePoint={setActivePoint}
                    setTranslateX={setTranslateX}
                    setTranslateY={setTranslateY}
                    setZoomLevel={setZoomLevel}
                    windowWidth={windowWidth}
                    buttonRef={(el) => (buttonRefs.current[index] = el)}
                    ariaLabelOpen={ariaLabelOpen}
                  />
                ))}
              </div>
            </div>

            <div className={`focal-point-content ${activePoint ? "show" : ""}`}>
              <button
                ref={closeButtonRef}
                className="close-icon"
                onClick={handleClose}
                aria-label={activePoint && ariaLabelClose ? ariaLabelClose.formatUnicorn({ title: activePoint?.ariaTitle }) : ""}
                tabIndex={activePoint ? 0 : -1}
              >
                <Svg iconName="close-new" />
              </button>

              <div data-epi-property="Title" className="rich-text">
                <h3>{ReactHtmlParser(activePoint?.title)}</h3>
                {ReactHtmlParser(activePoint?.information)}
              </div>

              {activePoint?.readMore && (
                <a
                  target={activePoint.target?.[0]?.value || "_blank"}
                  rel="noopener noreferrer"
                  href={activePoint.readMore}
                  tabIndex={activePoint ? 0 : -1}
                >
                  {activePoint.readMoreText}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function FocalPoint({
  point,
  index,
  active,
  selectedFocal,
  setSelectedFocal,
  setActivePoint,
  setTranslateX,
  setTranslateY,
  setZoomLevel,
  windowWidth,
  buttonRef,
  ariaLabelOpen,
}) {
  const iconStyle = {
    position: "absolute",
    top: `${point.yCoordinate}%`,
    left: `${point.xCoordinate}%`,
    cursor: "pointer",
  }

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

  const handleClick = () => {
    if (index === selectedFocal) {
      // close
      setSelectedFocal(null)
      setActivePoint(null)
      setZoomLevel(1)
      setTranslateX(0)
      setTranslateY(0)
    } else {
      // open
      setSelectedFocal(index)
      setActivePoint(point)
      const zoom = 1.5
      setZoomLevel(zoom)

      if (windowWidth >= 768) {
        // do your existing translate calc here…
        if (point.xCoordinate >= 16 && point.xCoordinate <= 46 && point.yCoordinate >= 16 && point.yCoordinate <= 78) {
          setTranslateX(0)
          setTranslateY(0)
        } else {
          let shiftX = 0
          if (point.xCoordinate < 16) shiftX = 16 - point.xCoordinate
          else if (point.xCoordinate > 46) shiftX = 46 - point.xCoordinate
          setTranslateX(clamp(shiftX, -50, 16))

          let shiftY = 0
          if (point.yCoordinate < 16) shiftY = 16 - point.yCoordinate
          else if (point.yCoordinate > 78) shiftY = 78 - point.yCoordinate
          setTranslateY(clamp(shiftY, -16, 16))
        }
      }
    }
  }

  return (
    <div className="focal-point-block-container">
      <button
        ref={buttonRef}
        style={iconStyle}
        onClick={handleClick}
        aria-label={ariaLabelOpen ? ariaLabelOpen.formatUnicorn({ title: point.ariaTitle }) : ""}
      >
        <Svg fillColor={active ? "#6a8ed5" : "#FFF"} iconName={active ? "focalpoint_active" : "focalpoint_inactive"} />
      </button>
    </div>
  )
}
